"use client"

import type React from "react"

import { useState, useEffect } from "react"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { getKeyDisplayName, HOTKEYS } from "@/lib/keyboard-utils"

interface KeyboardShortcutsTooltipProps {
  children: React.ReactNode
  showEasterEgg?: boolean
}

export function KeyboardShortcutsTooltip({ children, showEasterEgg = false }: KeyboardShortcutsTooltipProps) {
  const [isMounted, setIsMounted] = useState(false)

  // Only show on client-side to avoid hydration mismatch
  useEffect(() => {
    setIsMounted(true)
  }, [])

  // Get display names for the hotkeys
  const randomizeKey = getKeyDisplayName(HOTKEYS.RANDOMIZE)
  const easterEggKey = getKeyDisplayName(HOTKEYS.EASTER_EGG)

  if (!isMounted) return <>{children}</>

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{children}</TooltipTrigger>
        <TooltipContent
          side="bottom"
          align="end"
          className="w-48 p-4 bg-white rounded-xl outline outline-1 outline-offset-[-1px] outline-gray-200 overflow-hidden border-0 z-[100]"
        >
          <div className="self-stretch justify-start text-zinc-950 text-xs font-medium leading-tight mb-3">
            Keyboard Shortcut{showEasterEgg ? 's' : ''}
          </div>
          <div className="self-stretch flex flex-col justify-start items-start gap-2">
            <div className="self-stretch inline-flex justify-between items-center">
              <div className="justify-start text-gray-600 text-xs font-normal leading-tight">Randomize totem</div>
              <div className="w-5 h-5 p-1 bg-gray-100 rounded outline outline-[0.50px] outline-offset-[-0.50px] outline-gray-200 inline-flex flex-col justify-center items-center gap-1">
                <div className="text-center justify-start text-zinc-950 text-xs font-normal leading-none">
                  {randomizeKey}
                </div>
              </div>
            </div>

            {showEasterEgg && (
            <div className="self-stretch inline-flex justify-between items-center">
              <div className="justify-start text-gray-600 text-xs font-normal leading-tight">Happy Easter 🐣</div>
              <div className="w-5 h-5 p-1 bg-gray-100 rounded outline outline-[0.50px] outline-offset-[-0.50px] outline-gray-200 inline-flex flex-col justify-center items-center gap-1">
                <div className="text-center justify-start text-zinc-950 text-xs font-normal leading-none">
                  {easterEggKey}
                </div>
              </div>
            </div>
            )}
          </div>
          {showEasterEgg && (
          <div className="mt-3 text-xs text-gray-500">
            <p>Works with all keyboard layouts</p>
          </div>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}
